import { useState, useEffect, useReducer } from 'react'
import axios from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlag } from '@fs/zion-flags'
import { getCollectionDetails } from './gedx/recordService'

// turn on in browser console with: localStorage.debug = 'fs:zion-gedcomx:*'
const debug = zionDebug('zion-gedcomx:recordHooks')

/**
 * Hook to retrieve record and collection data for a record ark
 * Used in the Similar Records overlay for displaying persona information
 * Basic record information is provided in the API call for similar records, but it doesn't support the master template, so we need to get it again
 * @param {String} personaURL - record URL
 * @returns {Object|undefined} collection data
 */
export default function useRecordData(personaURL) {
  const [data, setData] = useState({})
  const [oldURL, setOldURL] = useState('') // we use a temp URL variable to prevent fetching data twice due to a re-render
  const recordsImprovementIndexing = useFeatureFlag('rip_search_imageIndexing')
  const fromSLS = recordsImprovementIndexing.isOn

  useEffect(() => {
    let cancelled = false
    if (personaURL && personaURL.indexOf('ark') > -1 && personaURL !== oldURL) {
      getRecordThenCollectionData(personaURL, fromSLS)
        .then((recordData) => {
          if (!cancelled) {
            debug(`record data for ${personaURL}:`, recordData)
            setOldURL(personaURL)
            setData(recordData)
          }
        })
        .catch(sendError)
    }
    return () => {
      cancelled = true
    }

    function sendError(response) {
      if (!cancelled) {
        debug(`error getting some data for ark ${personaURL}:`, response)
        setOldURL(personaURL)
        setData({
          error: response,
        })
      }
    }
  }, [personaURL, oldURL, fromSLS])

  return data
}

/**
 * Hook to retrieve record data only (for when collection data is not needed)
 * @param {String} personaURL - record URL
 * @returns {Object|undefined} collection data
 */
export function useFetchGedcomXData(personaURL) {
  // use a custom reducer initializer if default data is not static
  const [state, dispatch] = useReducer(loadingReducer, {
    data: null,
    loading: true,
    error: null,
  })
  useEffect(() => {
    let cancelled = false

    // Only fetch data if the URL exists; until then, stay in "loading"
    if (personaURL) {
      getGedcomXData(personaURL)
        .then((record) => {
          if (!cancelled) {
            debug('record response received', record)
            dispatch({ type: 'response', response: record })
          }
        })
        .catch((error) => {
          if (!cancelled) {
            debug('record errored', error)
            dispatch({ type: 'error', error })
          }
        })
    } else dispatch({ type: 'error' })

    return () => {
      cancelled = true
    }
  }, [personaURL])

  return [state.data, state.loading, state.error]
}

/**
 * Call for retrieving record and collection data
 *
 * @param {String} url - record or collection URL
 * @param {Boolean} fromSLS - record request is sent to the SLS server instead of CDS (recapi)
 * @returns {Promise} Axios response with record or collection data
 */
export async function getGedcomXData(url, fromSLS = false) {
  // we need to use our own instance to prevent auto-login
  const axiosInstance = axios.create()
  const headers = { Accept: 'application/x-gedcomx-v1+json', 'x-fs-feature-tag': 'collectionAPI' }

  let newUrl = url
  if (fromSLS) {
    // The Platform Team (Dave Nash), started out with forwarding the param to using x-fs-feature-tag.
    // I am using both because he is going to possibly remove the param and start using the feature tag instead
    newUrl = newUrl.includes('?') ? `${newUrl}&useSLS=true` : `${newUrl}?useSLS=true`
    headers['x-fs-feature-tag'] = 'records.use.sls'
  }

  return axiosInstance.get(newUrl, {
    headers,
  })
}

/**
 * Get the record and collection data and the collection wiki URL
 * We get the record first because the collection and wiki APIs require record data in the URL
 *
 * @param {String} personaURL - ARK URL for the persona
 * @param {Boolean} fromSLS - record request is sent to the SLS server instead of CDS (recapi)
 * @returns {Promise<{collection: Object, record: Object, collectionId: String, collectionURL: String}>}
 */
async function getRecordThenCollectionData(personaURL, fromSLS = false) {
  // First get record data (await will throw & exit the function if it fails
  const { data: record } = await getGedcomXData(personaURL, fromSLS)
  debug('returned record data:', record)

  // If something happened with the record data, error out; nothing else will work
  const { collectionURL, collectionId } = getCollectionDetails(record)
  if (!collectionURL) {
    return Promise.reject(new Error(`Error getting collection URL for ${personaURL}`))
  }

  // get collection after getting record data
  const { data: collection } = await getGedcomXData(collectionURL, fromSLS)

  return { record, collection, collectionId, collectionURL }
}

/**
 * Reducer to handle GedcomX data loading state
 *
 * @param {Object} state - original state
 * @param {Object} action - action to take
 * @returns {{data: null, loading: boolean, error: *}|{data, loading: boolean, error: null}} new state
 */
function loadingReducer(state, action) {
  switch (action.type) {
    case 'response':
      return {
        data: action.response.data,
        loading: false,
        error: null,
      }
    case 'error':
      return {
        data: null, // if you can fall back from this API gracefully, by all means put default data in here
        loading: false,
        error: action?.error,
      }
    default:
      // this code will never be executed if your reducer is scoped properly
      throw new Error(`Loading Reducer: no match for action type${action && action.type}`)
  }
}
